import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Fuel, Settings, MapPin, Phone, MessageCircle, Mail, ChevronLeft, ChevronRight, Heart, AlertCircle, LogIn, X, Eye } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiService, Car } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cars, dealers } from '@/data/cars';
import { apiUrl } from '@/lib/apiBase';
import { normalizeCar, getCarOwnerId } from '@/lib/normalizeCar';
import { usePageSeo } from '@/lib/usePageSeo';
import { JsonLd } from '@/components/seo/JsonLd';
import { carProductSchema } from '@/lib/schema';
import { absoluteUrl, buildCarListingDescription, buildCarListingTitle } from '@/lib/seo';
import { useListingViews } from '@/lib/useListingViews';
import { useFavorites } from '@/contexts/FavoritesContext';

const PLACEHOLDER_IMAGE = '/placeholder-car.svg';

const CarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { isLiked, toggleLike } = useFavorites();
  const carId = car?._id || id || '';
  const isFavorited = carId ? isLiked(String(carId)) : false;
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const viewRecordedFor = useRef<string | null>(null);
  const dealer = dealers[0]; // For demo, using first dealer

  const currentUserId = user?.id || (user as { _id?: string })?._id;
  const isAdmin = !!user && (Boolean(user.is_admin) || user.role === 'admin');
  const ownerId = car ? getCarOwnerId(car) : null;
  const isOwner =
    !!currentUserId && !!ownerId && String(ownerId) === String(currentUserId);
  const canManageListing = isAdmin || isOwner;

  const handleViewUpdate = useCallback(
    (payload: { carId: string; viewCount: number }) => {
      if (payload.carId === id) {
        setViewCount(payload.viewCount);
        setCar((prev) =>
          prev ? { ...prev, viewCount: payload.viewCount } : prev
        );
      }
    },
    [id]
  );

  useListingViews(handleViewUpdate, !!id && canManageListing);

  useEffect(() => {
    loadCar();
  }, [id, user]); // Re-fetch when user changes (login/logout)

  // Record a view when listing loads (once per car id per session)
  useEffect(() => {
    if (!car?._id || viewRecordedFor.current === car._id) return;
    viewRecordedFor.current = car._id;
    if (car.viewCount !== undefined) {
      setViewCount(car.viewCount);
    }

    apiService
      .recordCarView(car._id)
      .then((count) => {
        if (count === undefined) return;
        setViewCount(count);
        setCar((prev) => (prev ? { ...prev, viewCount: count } : prev));
      })
      .catch(() => {
        /* non-blocking */
      });
  }, [car?._id, car?.viewCount]);

  const setCarNormalized = (raw: Record<string, unknown>) => {
    setCar(normalizeCar(raw));
  };

  const loadCar = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prefer fetching a single car by id from the API
      if (id) {
        try {
          const apiCar = await apiService.getCarById(id);
          if (apiCar) {
            setCar(apiCar);
            return;
          }
        } catch (e) {
          // Continue to fallback paths below
        }
      }

      // Fallback: load all cars and try to find it when endpoint not available
      try {
        const maybeCars = await apiService.getCars();
        const carsArray = Array.isArray(maybeCars)
          ? maybeCars
          : (Array.isArray((maybeCars as any)?.cars)
            ? (maybeCars as any).cars
            : Array.isArray((maybeCars as any)?.data)
              ? (maybeCars as any).data
              : []);
        const foundCar = carsArray.find((c: any) => c?._id === id || c?.id === id);
        if (foundCar) {
          setCarNormalized(foundCar as Record<string, unknown>);
          return;
        }
      } catch (e) {
        // Ignore and proceed to static fallback
      }

      // Static data fallback
      const staticCar = cars.find(c => c.id === id);
      if (staticCar) {
        setCarNormalized(staticCar as unknown as Record<string, unknown>);
      } else {
        setError('Listing not found');
      }
    } catch (error) {
      console.error('Error loading car:', error);
      setError('Failed to load listing. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const nextImage = () => {
    if (car && car.images) {
      setCurrentImageIndex((prev) => (prev + 1) % car.images.length);
    }
  };

  const prevImage = () => {
    if (car && car.images) {
      setCurrentImageIndex((prev) => (prev - 1 + car.images.length) % car.images.length);
    }
  };

  const getCurrentImage = () => {
    if (!car) return PLACEHOLDER_IMAGE;
    if (car.images && car.images.length > 0) {
      return car.images[currentImageIndex];
    }
    return car.image || PLACEHOLDER_IMAGE;
  };

  const cityLabel = car?.city || car?.location || 'Pakistan';
  const seoTitle = car
    ? buildCarListingTitle(car)
    : 'Car Listings for Rent in Pakistan | Kaar.Rentals';
  const seoDescription = car
    ? buildCarListingDescription(car)
    : 'Browse self drive car listings across Pakistan on Kaar.Rentals. Contact owners via WhatsApp.';
  usePageSeo({
    title: seoTitle,
    description: seoDescription,
    path: car ? `/car/${car._id}/details` : '/cars',
  });

  const handleStartEditPrice = () => {
    if (!car) return;
    const currentPrice = car.pricePerDay || car.price || 0;
    setPriceInput(currentPrice ? String(currentPrice) : '');
    setEditingPrice(true);
  };

  const handleSavePrice = async () => {
    if (!car) return;
    try {
      if (!token) {
        toast({
          title: 'Authentication Required',
          description: 'Please sign in to update the price',
          variant: 'destructive',
        });
        return;
      }

      const value = Number(priceInput);
      if (!priceInput || isNaN(value) || value <= 0) {
        toast({
          title: 'Invalid price',
          description: 'Please enter a valid positive number for price.',
          variant: 'destructive',
        });
        return;
      }

      setSavingPrice(true);
      const updatedCar = await apiService.updateCarPrice(car._id, value, token);
      setCar(normalizeCar(updatedCar as unknown as Record<string, unknown>));

      toast({
        title: 'Price Updated',
        description: `Daily price updated to PKR ${value.toLocaleString()}`,
      });

      setEditingPrice(false);
    } catch (err: any) {
      toast({
        title: 'Update Failed',
        description: err.message || 'Failed to update price. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingPrice(false);
    }
  };

  const handleContactOwner = async () => {
    if (!car) return;

    if (!user) {
      setLoginModalOpen(true);
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/cars/${car._id || car.id}/contact`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        setLoginModalOpen(true);
        return;
      }

      if (response.status === 404) {
        const errorData = await response.json().catch(() => ({
          message: 'Owner phone not available',
        }));
        toast({
          title: 'Contact Unavailable',
          description: errorData.message || 'Owner phone not available',
          variant: 'destructive',
        });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch contact');
      }

      const data = await response.json();
      setContactPhone(data.phone);
      setContactModalOpen(true);
      try {
        await apiService.logCarInquiry(
          car._id,
          'Contacted owner from listing page',
          token
        );
      } catch {
        /* non-blocking */
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch contact information';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 md:pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading car details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 md:pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center px-4">
            <div className="mb-6">
              <div className="mx-auto w-24 h-24 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-foreground">
                {error || 'Listing not found'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {error === 'Listing not found'
                  ? "The car listing you're looking for doesn't exist or may have been removed."
                  : 'Something went wrong while loading the listing details.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/cars">
                <Button>Browse All Cars</Button>
              </Link>
              <Button variant="outline" onClick={() => loadCar()}>
                Try Again
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const features = car.features || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <JsonLd
        data={carProductSchema({
          brand: car.brand,
          model: car.model,
          year: car.year,
          description: car.description,
          images: car.images,
          city: car.city,
          location: car.location,
          pricePerDay: car.pricePerDay,
          price: car.price,
          priceType: car.priceType,
          status: car.status,
          category: car.category,
          url: absoluteUrl(`/car/${car._id}/details`),
          ownerName: car.owner?.name,
          ownerPhone: car.owner?.phone,
        })}
      />
      <Header />
      <main className="pt-16 md:pt-20 pb-24 lg:pb-0">
        {/* Breadcrumb */}
        <section className="py-4 bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/cars" className="inline-flex items-center text-muted-foreground hover:text-accent transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Cars
            </Link>
          </div>
        </section>

        {/* Car Details - Full Width Layout */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Image Gallery - 2/3 width */}
              <div className="lg:col-span-2 space-y-4">
                <div className="relative group">
                  <img 
                    src={getCurrentImage()} 
                    alt={`${car.brand} ${car.model}`}
                    className="w-full h-56 sm:h-80 lg:h-[500px] object-cover rounded-xl cursor-pointer"
                    onClick={() => {
                      // Open fullscreen image view
                      const newWindow = window.open(getCurrentImage(), '_blank');
                      if (newWindow) {
                        newWindow.document.write(`
                          <html>
                            <head><title>${car.brand} ${car.model}</title></head>
                            <body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;">
                              <img src="${getCurrentImage()}" style="max-width:100%;max-height:100vh;object-fit:contain;" />
                            </body>
                          </html>
                        `);
                      }
                    }}
                  />
                  
                  {/* Navigation Arrows */}
                  {car.images && car.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-card/90 hover:bg-card dark:bg-zinc-900/90 dark:hover:bg-zinc-800 rounded-full p-3 shadow-lg"
                      >
                        <ChevronLeft className="h-6 w-6 text-foreground" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-card/90 hover:bg-card dark:bg-zinc-900/90 dark:hover:bg-zinc-800 rounded-full p-3 shadow-lg"
                      >
                        <ChevronRight className="h-6 w-6 text-foreground" />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  {car.images && car.images.length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {car.images.length}
                    </div>
                  )}

                  {/* Brand Badge */}
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-zinc-900/90 dark:bg-zinc-950/90 text-white px-4 py-2 text-sm font-medium">
                      {car.brand}
                    </Badge>
                  </div>

                  {/* Heart Icon */}
                  <button 
                    onClick={() => car && void toggleLike(String(car._id || car.id))}
                    className="absolute top-4 right-4 bg-card/90 dark:bg-zinc-900/90 backdrop-blur-sm rounded-full p-3 hover:bg-card dark:hover:bg-zinc-800 transition-colors shadow-lg"
                    aria-label={isFavorited ? 'Remove from liked listings' : 'Add to liked listings'}
                  >
                    <Heart className={`h-5 w-5 ${isFavorited ? 'text-red-500 fill-current' : 'text-muted-foreground'}`} />
                  </button>
                </div>

                {/* Thumbnail Gallery */}
                {car.images && car.images.length > 1 && (
                  <div className="flex space-x-3 overflow-x-auto pb-2">
                    {car.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          index === currentImageIndex
                            ? 'border-accent ring-2 ring-accent/30'
                            : 'border-border hover:border-accent/50'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${car.brand} ${car.model} view ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Car Information - 1/3 width */}
              <div className="space-y-6">
                {/* Sticky Booking Card */}
                <div className="sticky top-24 bg-card rounded-xl shadow-lg border border-border p-6">
                  <div className="space-y-4">
                    {/* Price */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-foreground mb-1">
                        PKR {(car.pricePerDay || car.price || 0).toLocaleString()}
                        <span className="text-lg text-muted-foreground font-normal">
                          {car.priceType === 'monthly' ? '/month' : '/day'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">Best price guaranteed</p>
                    </div>

                    {/* Contact Owner Button */}
                    <div className="space-y-3">
                        <Button 
                          size="lg" 
                          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground dark:bg-accent dark:hover:bg-accent/90 dark:text-accent-foreground font-semibold py-3 min-h-11"
                        onClick={handleContactOwner}
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Contact Owner
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Car Information Section */}
        <section className="py-8 bg-muted/30 dark:bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Car Info - 2/3 width */}
              <div className="lg:col-span-2 space-y-8">
                {/* Car Title and Status */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h1 className="text-2xl sm:text-4xl font-bold text-foreground">
                    {car.brand} {car.model}
                  </h1>
                    {car.status && (
                      <Badge 
                        variant={car.status === 'rented' ? 'destructive' : 'default'}
                        className={car.status === 'rented'
                          ? 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300'}
                      >
                        {car.status === 'rented' ? 'Rented' : 'Available'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-muted-foreground mb-4">
                    <span className="text-lg">{car.year}</span>
                    <span>•</span>
                    <span className="text-lg">{car.engineCapacity}</span>
                    {canManageListing && (
                      <>
                        <span>•</span>
                        <span className="text-lg flex items-center gap-1.5">
                          <Eye className="h-4 w-4" />
                          {viewCount.toLocaleString()} views
                        </span>
                      </>
                    )}
                    {(car.city || car.location) && (
                      <>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-4 w-4" />
                          <span className="text-lg">{car.city || car.location}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-lg text-muted-foreground leading-relaxed">{car.description}</p>
                </div>

                {/* Quick Specs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-muted dark:bg-zinc-900/60 border border-border rounded-lg p-4 text-center">
                    <User className="h-8 w-8 mx-auto mb-2 text-blue-600 dark:text-accent" />
                    <div className="font-semibold text-foreground">{car.seating} Seats</div>
                    <div className="text-sm text-muted-foreground">Passengers</div>
                  </div>
                  <div className="bg-muted dark:bg-zinc-900/60 border border-border rounded-lg p-4 text-center">
                    <Fuel className="h-8 w-8 mx-auto mb-2 text-green-600 dark:text-accent" />
                    <div className="font-semibold text-foreground">{car.mileage}</div>
                    <div className="text-sm text-muted-foreground">Fuel Economy</div>
                  </div>
                  <div className="bg-muted dark:bg-zinc-900/60 border border-border rounded-lg p-4 text-center">
                    <Settings className="h-8 w-8 mx-auto mb-2 text-accent" />
                    <div className="font-semibold text-foreground">{car.transmission}</div>
                    <div className="text-sm text-muted-foreground">Transmission</div>
                  </div>
                  <div className="bg-muted dark:bg-zinc-900/60 border border-border rounded-lg p-4 text-center">
                    <div className="h-8 w-8 mx-auto mb-2 bg-accent/15 dark:bg-accent/20 rounded-full flex items-center justify-center">
                      <span className="text-accent font-bold text-sm">{car.category}</span>
                    </div>
                    <div className="font-semibold text-foreground">{car.category}</div>
                    <div className="text-sm text-muted-foreground">Vehicle Type</div>
                  </div>
                </div>

                {/* Features & Specifications */}
                <div className="space-y-8">
                  {/* Features */}
                  <div className="premium-card p-8">
                    <h2 className="text-2xl font-bold mb-6">Features & Equipment</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-accent rounded-full"></div>
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Specifications */}
                  <div className="premium-card p-8">
                    <h2 className="text-2xl font-bold mb-6">Specifications</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Engine</span>
                          <span className="font-semibold">{car.engineCapacity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fuel Type</span>
                          <span className="font-semibold">{car.fuelType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Transmission</span>
                          <span className="font-semibold">{car.transmission}</span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Year</span>
                          <span className="font-semibold">{car.year}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Mileage</span>
                          <span className="font-semibold">{car.mileage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Seating</span>
                          <span className="font-semibold">{car.seating} persons</span>
                        </div>
                        {(car.city || car.location) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Location</span>
                            <span className="font-semibold flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {car.city || car.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Owner Information - 1/3 width */}
              <div className="space-y-6">
                <div className="bg-card rounded-xl shadow-lg border border-border p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Owner Details</h3>
                  {car.owner?.unique_id ? (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-accent/15 dark:bg-accent/20 rounded-full flex items-center justify-center">
                          <span className="text-accent font-bold text-lg">
                            {(car.owner.name || 'O').charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg text-foreground">
                            {car.owner.name || 'Verified Owner'}
                          </h4>
                          {car.owner.location && (
                            <p className="text-sm text-muted-foreground">{car.owner.location}</p>
                          )}
                        </div>
                      </div>

                      <Link to={`/profile/${car.owner.unique_id}`}>
                        <Button variant="outline" className="w-full min-h-11">
                          <User className="h-4 w-4 mr-2" />
                          View owner profile
                        </Button>
                      </Link>
                      <p className="text-xs text-muted-foreground text-center">
                        See all listings from this owner
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 text-center">
                      <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
                      <p className="text-muted-foreground">
                        Owner profile is not available for this listing.
                      </p>
                    </div>
                  )}
                </div>

                {/* Status & Pricing - Owner/Admin Only */}
                {canManageListing && (
                  <>
                    <div className="bg-card rounded-xl shadow-lg border border-border p-6">
                      <h3 className="text-xl font-semibold mb-4 text-foreground">Listing Status</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Current Status:</span>
                          <Badge 
                            variant={car.status === 'rented' ? 'destructive' : 'default'}
                            className={car.status === 'rented'
                              ? 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                              : 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300'}
                          >
                            {car.status === 'rented' ? 'Rented' : 'Available'}
                          </Badge>
                        </div>
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={async () => {
                            if (!token) {
                              toast({
                                title: "Authentication Required",
                                description: "Please sign in to update listing status",
                                variant: "destructive",
                              });
                              return;
                            }
                            try {
                              const newStatus = car.status === 'rented' ? 'available' : 'rented';
                              const response = await fetch(apiUrl(`/api/cars/${car._id}/status`), {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`,
                                },
                                body: JSON.stringify({ status: newStatus }),
                              });
                              if (response.ok) {
                                const updatedCar = await response.json();
                                setCar(normalizeCar(updatedCar as Record<string, unknown>));
                                toast({
                                  title: "Status Updated",
                                  description: `Listing marked as ${newStatus}`,
                                });
                              } else {
                                throw new Error('Failed to update status');
                              }
                            } catch (err: any) {
                              toast({
                                title: "Update Failed",
                                description: err.message || "Failed to update listing status",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          Change status to {car.status === 'rented' ? 'Available' : 'Rented'}
                        </Button>
                      </div>
                    </div>

                    <div className="bg-card rounded-xl shadow-lg border border-border p-6">
                      <h3 className="text-xl font-semibold mb-4 text-foreground">Manage Pricing</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Current Price:</span>
                          <span className="font-semibold text-foreground">
                            PKR {(car.pricePerDay || car.price || 0).toLocaleString()}
                            <span className="text-sm text-muted-foreground">
                              {car.priceType === 'monthly' ? '/month' : '/day'}
                            </span>
                          </span>
                        </div>

                        {editingPrice ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={1}
                                value={priceInput}
                                onChange={(e) => setPriceInput(e.target.value)}
                                className="h-9"
                                placeholder="Enter new price"
                              />
                              <span className="text-sm text-muted-foreground">
                                {car.priceType === 'monthly' ? 'PKR/month' : 'PKR/day'}
                              </span>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setEditingPrice(false);
                                  setPriceInput('');
                                }}
                                disabled={savingPrice}
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={handleSavePrice}
                                disabled={savingPrice}
                              >
                                {savingPrice ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                  'Save Price'
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleStartEditPrice}
                          >
                            Change Price
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Additional Services */}
                <div className="premium-card p-6">
                  <h3 className="text-lg font-semibold mb-4">Additional Services</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                      <span>Free delivery within city</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                      <span>24/7 roadside assistance</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                      <span>Comprehensive insurance</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                      <span>Flexible cancellation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Mobile sticky contact bar */}
      {car && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-3 max-w-7xl mx-auto">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground truncate">
                {car.brand} {car.model}
              </p>
              <p className="text-lg font-bold text-foreground">
                PKR {(car.pricePerDay || car.price || 0).toLocaleString()}
                <span className="text-sm font-normal text-muted-foreground">
                  {car.priceType === 'monthly' ? '/month' : '/day'}
                </span>
              </p>
            </div>
            <Button
              size="lg"
              className="shrink-0 min-h-11 px-5 bg-primary hover:bg-primary/90 text-primary-foreground dark:bg-accent dark:hover:bg-accent/90 dark:text-accent-foreground"
              onClick={handleContactOwner}
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact
            </Button>
          </div>
        </div>
      )}

      <Footer />

      {/* Login Modal */}
      <Dialog open={loginModalOpen} onOpenChange={setLoginModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign In Required</DialogTitle>
            <DialogDescription>
              Please sign in to view the owner's contact information.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <Button 
              className="w-full"
              onClick={() => {
                setLoginModalOpen(false);
                navigate('/auth?mode=login');
              }}
            >
              Go to Sign In
            </Button>
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => setLoginModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Phone Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Owner Contact</DialogTitle>
            <DialogDescription>
              Contact the owner directly
            </DialogDescription>
          </DialogHeader>
          {contactPhone && (
            <div className="mt-4 space-y-4">
              <div className="text-center">
                <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
                <p className="text-2xl font-bold text-foreground">{contactPhone}</p>
              </div>
              <div className="flex space-x-2">
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const whatsapp = contactPhone.replace(/[^0-9]/g, '');
                    window.open(`https://wa.me/${whatsapp}`, '_blank');
                  }}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    window.open(`tel:${contactPhone}`, '_self');
                  }}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
              </div>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => setContactModalOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarDetails;